import { AccountIdMeta, WithTodoFragment, completedStatuses } from 'components/dashboard/Reminders'
import {
  AllTodosDocument,
  ClaimAccountRollupFragment,
  TodoItemStatusType,
  useClaimAccountRollupQuery,
  useDismissReminderMutation,
  useQuickAssignFirstOwnerMutation,
  useRestoreReminderMutation,
} from 'graphql/bindings/AdminBindings'
import {
  CheckCircleIcon,
  ClockIcon,
  EllipsisVIcon,
  FlagIcon,
  Link,
  NoteFieldRow,
  SignalIcon,
  SingleInfoCard,
  TimesIcon,
  Typography,
  UserIcon,
  UsersIcon,
  useModal,
  useTheme,
} from '@forgeglobal/design-framework'
import { ClaimAccountModal } from 'components/dashboard/ClaimAccountModal'
import { PopoverAction, PopoverListItem, PopoverListItemIcon, PopoverListItemText } from 'isolated-design-library'
import { Routes } from 'util/Routes'
import { getGravatarUrl } from 'util/GravatarUtil'
import React, { Fragment } from 'react'
import moment from 'moment'

interface ClaimAccountMeta {
  ClaimAccount: AccountIdMeta
}

const getCreatedPrefix = (source: string) => {
  switch (source) {
    case 'crm_admin':
      return 'Added to Market Console'
    case 'platform_user':
      return 'Signed up via platform'
    default:
      return 'Created by system'
  }
}

type ClaimAccountNotesProps = WithTodoFragment & { accountOpt?: ClaimAccountRollupFragment; currentUserId?: number }
const ClaimCompletedText: React.FC<ClaimAccountNotesProps> = ({ todo, accountOpt, currentUserId }) => {
  const account = accountOpt
  const finishedBy = todo?.finished_by
  const finishedByMe = finishedBy?.id === currentUserId
  const owners = account?.owners ?? []
  const ownedByMe = owners.some(owner => owner.admin_id === currentUserId)
  const completedAt = moment(todo.completed_at)
  const firstOwner = owners[0]
  const ownerName = firstOwner?.name
  const finishedByName = `${finishedBy?.first_name} ${finishedBy?.last_name}`
  const completedTimeString = moment().isSame(completedAt, 'day')
    ? `today at ${completedAt.format('h:mm a')}`
    : `on ${completedAt.format('M/D/YY')}`
  if (ownedByMe && finishedByMe) {
    return <Fragment>{`You claimed ownership ${completedTimeString}`}</Fragment>
  } else if (finishedByMe) {
    return <Fragment>{`You assigned ownership to ${ownerName} ${completedTimeString}`}</Fragment>
  } else if (ownedByMe) {
    return <Fragment>{`Ownership was assigned to you by ${finishedByName} ${completedTimeString}`}</Fragment>
  }
  return <Fragment>{`Ownership was assigned to ${ownerName} by ${finishedByName} ${completedTimeString}`}</Fragment>
}

const ClaimAccountNotes: React.FC<ClaimAccountNotesProps> | null = ({ todo, accountOpt, currentUserId }) => {
  const account = accountOpt
  const theme = useTheme()
  const [restoreReminder] = useRestoreReminderMutation({
    variables: { reminderId: todo.id },
    refetchQueries: [{ query: AllTodosDocument }],
  })
  const accountId = account?.id ?? ''
  const [quickClaim] = useQuickAssignFirstOwnerMutation({
    variables: { accountId: accountId, ownerId: currentUserId! },
    refetchQueries: [{ query: AllTodosDocument }],
  })

  const [showModal, hideModal] = useModal(() => {
    return <ClaimAccountModal hideModal={hideModal} accountId={accountId} accountName={account?.name ?? ''} />
  }, [accountId])

  if (todo.status === TodoItemStatusType.Dismissed || !!todo.dismissed_at) {
    return (
      <NoteFieldRow icon={TimesIcon}>
        You dismissed this reminder.
        <br />
        We won&apos;t remind you about this task again.
        <br />
        <Typography color="primary" variant="caption">
          <Link onClick={() => restoreReminder()}>
            <b>Restore this reminder.</b>
          </Link>
        </Typography>
      </NoteFieldRow>
    )
  }
  switch (todo.status) {
    case TodoItemStatusType.Live:
    case TodoItemStatusType.Upcoming:
      const isAOI = account?.is_account_of_interest ?? false
      const AOIBy = account?.account_of_interest_set_by_admin?.name ?? ''
      const AOIReason = account?.account_of_interest_reason ?? ''
      const AOINote = (!!AOIBy || !!AOIReason) && (
        <NoteFieldRow icon={FlagIcon} iconColor={theme.palette.secondary.main}>
          {AOIBy && `Set by ${AOIBy}. `}
          {AOIReason}
        </NoteFieldRow>
      )
      const createdAtFromNow = moment(account?.created_at!).fromNow()
      const createdByName = account?.admin_created_by?.name
      const createdByText = createdByName ? ` by ${createdByName}` : ''
      const createdNote = (
        <NoteFieldRow icon={ClockIcon}>
          {getCreatedPrefix(account?.source ?? '')}
          {createdByText} {createdAtFromNow}
        </NoteFieldRow>
      )
      return (
        <Fragment>
          {isAOI && AOINote}
          {isAOI && createdNote}
          {!isAOI && <NoteFieldRow icon={SignalIcon}>Investor level {account?.account_level}</NoteFieldRow>}
          <NoteFieldRow icon={UserIcon}>
            This account needs an owner.
            <br />
            <Typography color="primary" variant="caption">
              <Link onClick={() => quickClaim()}>
                <b>Assign to me</b>
              </Link>
            </Typography>{' '}
            |
            <Typography color="primary" variant="caption">
              <Link onClick={showModal}>
                {' '}
                <b>Assign to someone else</b>
              </Link>
            </Typography>
          </NoteFieldRow>
        </Fragment>
      )
    case TodoItemStatusType.Completed:
      return (
        <NoteFieldRow iconColor={theme.palette.secondary.dark} icon={CheckCircleIcon}>
          <ClaimCompletedText todo={todo} accountOpt={accountOpt} currentUserId={currentUserId} />
        </NoteFieldRow>
      )
    default:
      return null
  }
}

export const ClaimAccountTodoCard: React.FC<WithTodoFragment> = ({ todo }) => {
  const claimMeta = JSON.parse(todo.meta) as ClaimAccountMeta
  const accountId = claimMeta.ClaimAccount.accountId
  const { data } = useClaimAccountRollupQuery({ variables: { accountId } })
  const account = data?.account
  const isAOI = account?.is_account_of_interest ?? false
  const accountImg = account?.img || getGravatarUrl(account?.email ?? '')
  const [dismissReminder] = useDismissReminderMutation({
    variables: { reminderId: todo.id },
    refetchQueries: [{ query: AllTodosDocument }],
  })
  const [quickClaim] = useQuickAssignFirstOwnerMutation({
    variables: { accountId, ownerId: data?.currentAdmin?.id! },
    refetchQueries: [{ query: AllTodosDocument }],
  })
  const [showModal, hideModal] = useModal(() => {
    return <ClaimAccountModal hideModal={hideModal} accountId={accountId} accountName={account?.name ?? ''} />
  }, [accountId])
  const isDone = !!todo.dismissed_at || completedStatuses.includes(todo.status)
  const actions = (
    <Fragment>
      <PopoverListItem button onClick={() => quickClaim()}>
        <PopoverListItemIcon>
          <UserIcon />
        </PopoverListItemIcon>
        <PopoverListItemText>Assign to me</PopoverListItemText>
      </PopoverListItem>
      <PopoverListItem button onClick={showModal}>
        <PopoverListItemIcon>
          <UsersIcon />
        </PopoverListItemIcon>
        <PopoverListItemText>Assign to someone</PopoverListItemText>
      </PopoverListItem>
      <PopoverListItem button onClick={() => dismissReminder()}>
        <PopoverListItemIcon>
          <TimesIcon />
        </PopoverListItemIcon>
        <PopoverListItemText>Dismiss</PopoverListItemText>
      </PopoverListItem>
    </Fragment>
  )
  return (
    <SingleInfoCard
      logo={accountImg}
      leftTitle={account?.name}
      link={Routes.account(account?.id ?? '')}
      openInNewTab
      secondaryIcon={isAOI && <FlagIcon />}
      leftSubtitle={isAOI ? 'Account of Interest Assign ownership' : 'Assign ownership'}
      notes={<ClaimAccountNotes accountOpt={account} todo={todo} currentUserId={data?.currentAdmin?.id} />}
      action={!isDone && <PopoverAction icon={<EllipsisVIcon />} actionMenu={actions} />}
    />
  )
}
